import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeaderBannerPage from "../components/HeaderBannerPage";
import { fetchPmFrames, fetchPmIwoList, fetchPmDailyRemarks, fetchPmDelayReasons } from "../actions/ProductionMonitorAction";

/* Remark History
   IWO is required; Work Date, Component and Frame are optional.
   -> GetDailyRemarks?iwoNo=..(&workDate=..)(&frameId=..)   (component narrows it on screen)
   Shown date wise: date -> frames -> remarks
*/


const NAVY = "#142c54";
const BLUE = "#154c79";

// SHELL_GIRDER -> Shell Girder, DECK_01 -> Deck 01
// names that don't follow the simple "_" -> space rule
const COMPONENT_NAMES = { EDGE_GRINDING: "EDGE Grinding" };

const componentLabel = (v) =>
  COMPONENT_NAMES[v] ||
  String(v || "")
    .split("_")
    .map((w) => (w.length > 3 ? w.charAt(0) + w.slice(1).toLowerCase() : w))
    .join(" ");

const toYmd = (v) => String(v || "").slice(0, 10);

// typed digits -> yyyy-MM-dd
const formatTypedDate = (text) => {
  const digits = String(text || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
};

const isValidYmd = (v) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const [y, m, d] = v.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

const toNumber = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const cardSx = {
  p: { xs: 1.5, sm: 2.5 },
  borderRadius: "16px",
  border: "1px solid #d6dce5",
  backgroundColor: "#fff",
  boxShadow: "0px 4px 12px rgba(0,0,0,0.06)",
};

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "12px", backgroundColor: "#fff" } };

// Same CDL reason groups as Update Daily Remark
const CDL_REASON_GROUPS = {
  SF: "SWC Workshop Delay",
  SC: "SWC Workshop Delay",
  SB: "SWC Workshop Delay",
  ST: "SWC Workshop Delay",
  SM: "SWC Workshop Delay",
  SL: "SWC Workshop Delay",
  IP: "Inspection Delay",
  IQ: "Inspection Delay",
  IO: "Inspection Delay",
};
const cdlGroupOf = (code) => CDL_REASON_GROUPS[code] || "";

// "SWC Workshop Delay · Material Delay" (or just the name when no group)
const reasonText = (list, code, groupOf) => {
  if (!code) return "";
  const name = (list || []).find((d) => d.Code === code)?.Name || code;
  const group = groupOf ? groupOf(code) : "";
  return group ? `${group} · ${name}` : name;
};

/* ---- One remark ---- */
const RemarkCard = ({ r, dtsDelayReasons, cdlDelayReasons }) => (
  <Box sx={{ p: 1.5, borderRadius: "12px", border: "1px solid #eef1f5", backgroundColor: "#fafbfd" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      {/* the date is in the heading above - show the time it was added */}
      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#0E3C66" }}>
        {String(r.CreatedDate || "").slice(11, 16) || toYmd(r.WorkDate)}
      </Typography>
      <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
        <Chip label={`DTS ${toNumber(r.LostHoursDts)} h`} size="small" sx={{ height: 22, fontSize: "11px" }} />
        <Chip label={`CDL ${toNumber(r.LostHoursCdl)} h`} size="small" sx={{ height: 22, fontSize: "11px" }} />
      </Box>
    </Box>
    {r.Remark && (
      <Typography sx={{ fontSize: "13px", color: "#333", mt: 0.75, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {r.Remark}
      </Typography>
    )}
    {[
      { label: "DTS", code: r.DtsDelayReason, name: r.DtsDelayReasonName, remark: r.DtsRemark, list: dtsDelayReasons },
      { label: "CDL", code: r.CdlDelayReason, name: r.CdlDelayReasonName, remark: r.CdlRemark, list: cdlDelayReasons, groupOf: cdlGroupOf },
    ]
      .filter((x) => x.code || x.remark)
      .map((x) => {
        // reason name from the API, with the common group name (SWC Workshop Delay / Inspection Delay)
        const group = x.groupOf && x.code ? x.groupOf(x.code) : "";
        const reason = x.code ? (x.name ? (group ? `${group} · ${x.name}` : x.name) : reasonText(x.list, x.code, x.groupOf)) : "";
        return (
          <Typography key={x.label} sx={{ fontSize: "12px", color: "#555", mt: 0.5 }}>
            <b style={{ color: NAVY }}>{x.label}:</b> {reason}
            {reason && x.remark ? " — " : ""}
            {x.remark}
          </Typography>
        );
      })}
    <Typography sx={{ fontSize: "11px", color: "#9aa3b2", mt: 0.5 }}>
      By {r.CreatedByName ? `${r.CreatedByName} (${r.CreatedBy})` : r.CreatedBy} · {r.CreatedDate}
      {r.UpdatedDate ? ` · edited ${r.UpdatedDate}${r.UpdatedByName ? ` by ${r.UpdatedByName}` : ""}` : ""}
    </Typography>
  </Box>
);

/* -------------------- Page -------------------- */
const RemarkHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const {
    frames: frameRows,
    framesLoading,
    framesError,
    iwoList,
    dailyRemarks,
    dailyRemarksLoading,
    dailyRemarksError,
    cdlDelayReasons,
    dtsDelayReasons,
  } = useSelector((state) => state.productionMonitor);

  // what was selected on Update Daily Remark (kept for the Back button)
  const fromIwoNo = params.get("iwoNo") || null;
  const fromFrameId = params.get("frameId") ? Number(params.get("frameId")) : null;

  const [iwoNo, setIwoNo] = useState(fromIwoNo);
  const [component, setComponent] = useState(null); // null = all components of the IWO
  const [frameId, setFrameId] = useState(fromFrameId); // null = all frames of the component
  const [workDate, setWorkDate] = useState(""); // empty = all dates
  const datePickerRef = useRef(null);

  useEffect(() => {
    dispatch(fetchPmFrames());
    dispatch(fetchPmIwoList()); // Eng Spec for the IWO list
    dispatch(fetchPmDelayReasons());
  }, [dispatch]);

  const frames = useMemo(
    () =>
      [...(frameRows || [])].sort(
        (a, b) =>
          String(a.IwoNo).localeCompare(String(b.IwoNo), undefined, { numeric: true }) ||
          String(a.FrameNo).localeCompare(String(b.FrameNo), undefined, { numeric: true })
      ),
    [frameRows]
  );

  // one row per IWO with Eng Spec + frame count
  const iwoOptions = useMemo(() => {
    const map = new Map();
    frames.forEach((f) => {
      const key = String(f.IwoNo);
      if (!map.has(key)) {
        const fromList = (iwoList || []).find((x) => String(x.IwoNo) === key);
        map.set(key, { IwoNo: key, EngSpec: f.EngSpec || fromList?.EngSpec || "", frames: 0 });
      }
      map.get(key).frames += 1;
    });
    return Array.from(map.values());
  }, [frames, iwoList]);

  const iwo = iwoOptions.find((o) => o.IwoNo === iwoNo) || null;
  const iwoFrames = useMemo(() => (iwoNo ? frames.filter((f) => String(f.IwoNo) === iwoNo) : []), [frames, iwoNo]);

  // components of the chosen IWO (with frame counts)
  const componentOptions = useMemo(() => {
    const map = new Map();
    iwoFrames.forEach((f) => {
      const key = f.ComponentName || "";
      if (!map.has(key)) map.set(key, { value: key, frames: 0 });
      map.get(key).frames += 1;
    });
    return Array.from(map.values());
  }, [iwoFrames]);
  const componentOption = componentOptions.find((c) => c.value === component) || null;

  // frames of the chosen component
  const componentFrames = useMemo(
    () => (component !== null ? iwoFrames.filter((f) => (f.ComponentName || "") === component) : iwoFrames),
    [iwoFrames, component]
  );
  const frame = componentFrames.find((f) => f.FrameId === frameId) || null;

  // coming from Daily Remark with a frame -> also select that frame's component
  useEffect(() => {
    if (component === null && frameId) {
      const f = iwoFrames.find((x) => x.FrameId === frameId);
      if (f) setComponent(f.ComponentName || "");
    }
  }, [iwoFrames, frameId, component]);

  /* ---- load remarks of the IWO (date / frame optional) ---- */
  const dateOk = workDate === "" || isValidYmd(workDate);
  const queryFrameId = frameId || null;
  const queryDate = isValidYmd(workDate) ? workDate : "";
  const canLoad = !!iwoNo && dateOk;

  useEffect(() => {
    if (canLoad) dispatch(fetchPmDailyRemarks({ iwoNo, frameId: queryFrameId, workDate: queryDate }));
  }, [dispatch, canLoad, iwoNo, queryFrameId, queryDate]);

  // component chosen -> narrow on screen
  const remarks = useMemo(() => {
    if (!canLoad) return [];
    return (dailyRemarks || [])
      .filter((r) => String(r.IwoNo) === iwoNo)
      .filter((r) => component === null || (r.ComponentName || "") === component)
      .filter((r) => !queryFrameId || r.FrameId === queryFrameId);
  }, [dailyRemarks, canLoad, iwoNo, component, queryFrameId]);

  // date wise: newest date first -> frames of that date -> remarks (a frame can have many remarks a day)
  const days = useMemo(() => {
    const byDate = new Map();
    remarks.forEach((r) => {
      const d = toYmd(r.WorkDate);
      if (!byDate.has(d)) byDate.set(d, new Map());
      const frames = byDate.get(d);
      if (!frames.has(r.FrameId)) {
        frames.set(r.FrameId, { frameId: r.FrameId, frameNo: r.FrameNo, componentName: r.ComponentName, remarks: [] });
      }
      frames.get(r.FrameId).remarks.push(r);
    });
    return Array.from(byDate, ([date, frameMap]) => {
      const groups = Array.from(frameMap.values())
        .map((g) => ({ ...g, remarks: g.remarks.sort((a, b) => String(b.CreatedDate || "").localeCompare(String(a.CreatedDate || ""))) }))
        .sort(
          (a, b) =>
            String(a.componentName || "").localeCompare(String(b.componentName || "")) ||
            String(a.frameNo || "").localeCompare(String(b.frameNo || ""), undefined, { numeric: true })
        );
      const all = groups.flatMap((g) => g.remarks);
      return {
        date,
        groups,
        count: all.length,
        dts: all.reduce((n, r) => n + toNumber(r.LostHoursDts), 0),
        cdl: all.reduce((n, r) => n + toNumber(r.LostHoursCdl), 0),
      };
    }).sort((a, b) => b.date.localeCompare(a.date));
  }, [remarks]);

  const totalDts = remarks.reduce((sum, r) => sum + toNumber(r.LostHoursDts), 0);
  const totalCdl = remarks.reduce((sum, r) => sum + toNumber(r.LostHoursCdl), 0);
  const people = new Set(remarks.map((r) => r.CreatedBy)).size;

  // back to Update Daily Remark with the same IWO / frame it came from
  const goBack = () => {
    const q = new URLSearchParams();
    if (fromIwoNo) q.set("iwoNo", fromIwoNo);
    if (fromIwoNo && fromFrameId) q.set("frameId", String(fromFrameId));
    navigate(`/dailyRemark${q.toString() ? `?${q}` : ""}`);
  };

  const openDatePicker = () => {
    const el = datePickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else el.click();
  };

  const subtitle = [
    `IWO ${iwoNo}`,
    component !== null ? componentLabel(component) : "all components",
    frame ? frame.FrameNo || componentLabel(frame.ComponentName) : "all frames",
    queryDate || "all dates",
  ].join(" · ");

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f5f7fb" }}>
      <HeaderBannerPage />

      <Box
        sx={{
          maxWidth: "900px",
          width: "100%",
          mx: "auto",
          px: { xs: 1.5, sm: 2, md: 4 },
          py: { xs: 2, md: 3 },
          boxSizing: "border-box",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        {/* Title + back */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <HistoryIcon sx={{ color: BLUE }} />
          <Typography sx={{ fontSize: { xs: "19px", md: "22px" }, fontWeight: 700, color: "#0E3C66", letterSpacing: "0.5px" }}>
            Remark History
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            sx={{ ml: "auto", textTransform: "none", fontWeight: 700, borderRadius: "10px", color: BLUE, borderColor: "#c9d6e6", backgroundColor: "#fff" }}
          >
            Daily Remark
          </Button>
        </Box>

        {/* Filters */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) minmax(0, 1fr)" }, gap: 1.5 }}>
            {/* 1. IWO */}
            <Autocomplete
              sx={{ gridColumn: "1 / -1" }}
              options={iwoOptions}
              value={iwo}
              onChange={(_, v) => {
                setIwoNo(v ? v.IwoNo : null);
                setComponent(null);
                setFrameId(null);
              }}
              loading={framesLoading}
              autoHighlight
              getOptionLabel={(o) => (o ? `${o.IwoNo}${o.EngSpec ? ` - ${o.EngSpec}` : ""}` : "")}
              isOptionEqualToValue={(o, v) => o.IwoNo === v.IwoNo}
              filterOptions={(opts, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                if (!q) return opts;
                return opts.filter((o) => [o.IwoNo, o.EngSpec].some((v) => String(v || "").toLowerCase().includes(q)));
              }}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.IwoNo} sx={{ display: "block !important", py: "8px !important" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0E3C66" }}>{o.IwoNo}</Typography>
                    <Typography sx={{ ml: "auto", fontSize: "11px", color: "#6b7280", whiteSpace: "nowrap" }}>{o.frames} frame(s)</Typography>
                  </Box>
                  {o.EngSpec && (
                    <Typography sx={{ fontSize: "12px", color: "#555", whiteSpace: "normal", wordBreak: "break-word" }}>{o.EngSpec}</Typography>
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="IWO No - Eng Spec"
                  placeholder="Select an IWO"
                  InputLabelProps={{ shrink: true }}
                  helperText={iwo?.EngSpec || ""}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {framesLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Work date (optional) */}
            <Box sx={{ position: "relative", gridColumn: "1 / -1" }}>
              <TextField
                fullWidth
                label="Work Date"
                placeholder="All dates"
                value={workDate}
                onChange={(e) => setWorkDate(formatTypedDate(e.target.value))}
                error={!dateOk}
                helperText={!dateOk ? "Enter work date as yyyy-mm-dd" : workDate ? "" : "All dates"}
                InputLabelProps={{ shrink: true }}
                inputProps={{ inputMode: "numeric", maxLength: 10 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {workDate && (
                        <Tooltip title="All dates">
                          <IconButton size="small" onClick={() => setWorkDate("")} aria-label="Clear work date">
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small" edge="end" onClick={openDatePicker} aria-label="Pick work date">
                        <CalendarMonthIcon sx={{ color: BLUE }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />
              {/* hidden native picker - only opens the calendar, value stays yyyy-mm-dd */}
              <input
                ref={datePickerRef}
                type="date"
                tabIndex={-1}
                aria-hidden="true"
                value={isValidYmd(workDate) ? workDate : ""}
                onChange={(e) => e.target.value && setWorkDate(e.target.value)}
                style={{ position: "absolute", right: 12, top: 40, width: 1, height: 1, opacity: 0, border: 0, padding: 0, pointerEvents: "none" }}
              />
            </Box>

            {/* 2. Component */}
            <Autocomplete
              options={componentOptions}
              value={componentOption}
              onChange={(_, v) => {
                setComponent(v ? v.value : null);
                setFrameId(null);
              }}
              disabled={!iwoNo}
              autoHighlight
              getOptionLabel={(c) => (c ? componentLabel(c.value) || "-" : "")}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              noOptionsText="No components for this IWO"
              renderOption={(props, c) => (
                <Box component="li" {...props} key={c.value} sx={{ display: "flex !important", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0E3C66" }}>{componentLabel(c.value) || "-"}</Typography>
                  <Typography sx={{ ml: "auto", fontSize: "11px", color: "#6b7280", whiteSpace: "nowrap" }}>{c.frames} frame(s)</Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Component"
                  placeholder={iwoNo ? "All components" : "Select an IWO first"}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              )}
            />

            {/* 3. Frame */}
            <Autocomplete
              options={componentFrames}
              value={frame}
              onChange={(_, v) => setFrameId(v ? v.FrameId : null)}
              disabled={!iwoNo || component === null}
              autoHighlight
              getOptionLabel={(f) => (f ? `${f.FrameNo ? `${f.FrameNo} · ` : ""}${componentLabel(f.ComponentName)}` : "")}
              isOptionEqualToValue={(o, v) => o.FrameId === v.FrameId}
              noOptionsText="No frames for this component"
              renderOption={(props, f) => (
                <Box component="li" {...props} key={f.FrameId} sx={{ display: "flex !important", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0E3C66" }}>{f.FrameNo || "—"}</Typography>
                  <Chip label={componentLabel(f.ComponentName)} size="small" sx={{ height: 20, fontSize: "11px" }} />
                  {f.CompletionStatus && <Chip label="Completed" size="small" color="success" sx={{ height: 20, fontSize: "11px" }} />}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Frame"
                  placeholder={!iwoNo ? "Select an IWO first" : component === null ? "Select a component first" : "All frames"}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              )}
            />
          </Box>
        </Paper>

        {framesError && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{framesError}</Alert>}

        {!canLoad ? (
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={{ fontSize: "13px", color: "#9aa3b2", py: 5, textAlign: "center" }}>
              Select an IWO to see its daily remarks
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={{ fontSize: "12px", color: "#6b7280", mb: 1.25 }}>{subtitle}</Typography>

            {/* Totals */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, mb: 2 }}>
              {[
                { label: "Entries", value: remarks.length },
                { label: "People", value: people },
                { label: "Lost DTS", value: `${totalDts} h` },
                { label: "Lost CDL", value: `${totalCdl} h` },
              ].map((t) => (
                <Box key={t.label} sx={{ p: 1, borderRadius: "10px", backgroundColor: "#f3f6fa", textAlign: "center" }}>
                  <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>{t.label}</Typography>
                  <Typography sx={{ fontSize: "15px", fontWeight: 700, color: NAVY }}>{t.value}</Typography>
                </Box>
              ))}
            </Box>

            {dailyRemarksError && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{dailyRemarksError}</Alert>}

            {dailyRemarksLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, py: 4 }}>
                <CircularProgress size={20} />
                <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>Loading remarks…</Typography>
              </Box>
            ) : remarks.length === 0 ? (
              <Typography sx={{ fontSize: "13px", color: "#9aa3b2", py: 4, textAlign: "center" }}>No remarks found</Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {days.map((day) => (
                  <Box key={day.date}>
                    {/* date heading */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        px: 1.25,
                        py: 0.75,
                        mb: 1.25,
                        borderRadius: "10px",
                        backgroundColor: NAVY,
                        color: "#fff",
                        position: "sticky",
                        top: 64,
                        zIndex: 2,
                      }}
                    >
                      <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>{day.date}</Typography>
                      <Typography sx={{ ml: "auto", fontSize: "11px", opacity: 0.85 }}>
                        {day.groups.length} frame(s) · {day.count} remark(s) · DTS {day.dts} h · CDL {day.cdl} h
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pl: { sm: 1 } }}>
                      {day.groups.map((g) => {
                        const f = frames.find((x) => x.FrameId === g.frameId);
                        return (
                          <Box key={`${day.date}-${g.frameId}`}>
                            {/* frame heading */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75, flexWrap: "wrap" }}>
                              <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0E3C66" }}>{g.frameNo || "—"}</Typography>
                              <Chip label={componentLabel(g.componentName)} size="small" sx={{ height: 20, fontSize: "11px" }} />
                              <Typography sx={{ ml: "auto", fontSize: "11px", color: "#6b7280" }}>
                                {g.remarks.length} remark(s){f ? ` · ${Math.round(toNumber(f.PhysicalCompletion))}% done` : ""}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              {g.remarks.map((r, i) => (
                                <RemarkCard
                                  key={r.DailyId || `${g.frameId}-${i}`}
                                  r={r}
                                  dtsDelayReasons={dtsDelayReasons}
                                  cdlDelayReasons={cdlDelayReasons}
                                />
                              ))}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default RemarkHistory;